import { useMemo } from 'react'
import { format } from 'date-fns'
import {
	LineChart,
	Line,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
	Legend,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { Skeleton } from '@/common/ui/skeleton'
import { DataPoint } from '../../type'

interface MetricsChartProps {
	data: DataPoint[]
	title: string
	description?: string
	color: string
	isLoading?: boolean
	unit?: string
}

export default function MetricsChart({
	data,
	title,
	description,
	color,
	isLoading = false,
	unit = '%',
}: MetricsChartProps) {
	const chartData = useMemo(() => {
		if (!data || data.length === 0) return []

		return data.map((point) => ({
			time: format(new Date(point.timestamp), 'HH:mm'),
			value: point.value ?? 0,
			fullTime: format(new Date(point.timestamp), 'MMM dd, HH:mm'),
		}))
	}, [data])

	const yAxisDomain = useMemo(() => {
		if (!data || data.length === 0) return [0, 100]
		// For percentage units, use 0-100 range
		if (unit === '%') {
			return [0, 100]
		}
		const values = data
			.map((point) => point.value)
			.filter((v): v is number => v !== null && v !== undefined)
		if (values.length === 0) return [0, 100]
		const min = Math.min(...values)
		const max = Math.max(...values)
		// Add 10% padding to the top and bottom
		const padding = (max - min) * 0.1 || max * 0.1 || 10
		return [Math.max(0, min - padding), max + padding]
	}, [data, unit])

	if (isLoading) {
		return (
			<Card className="group border-2 transition-all duration-300 hover:shadow-lg">
				<CardHeader className="border-b bg-gradient-to-r from-muted/30 to-background">
					<CardTitle className="text-lg font-bold">{title}</CardTitle>
					{description && (
						<p className="mt-1 text-sm text-muted-foreground">{description}</p>
					)}
				</CardHeader>
				<CardContent className="pt-6">
					<Skeleton className="h-64 w-full rounded-lg" />
				</CardContent>
			</Card>
		)
	}

	if (!data || data.length === 0) {
		return (
			<Card className="group border-2 transition-all duration-300 hover:shadow-lg">
				<CardHeader className="border-b bg-gradient-to-r from-muted/30 to-background">
					<CardTitle className="text-lg font-bold">{title}</CardTitle>
					{description && (
						<p className="mt-1 text-sm text-muted-foreground">{description}</p>
					)}
				</CardHeader>
				<CardContent className="pt-6">
					<div className="flex h-64 items-center justify-center rounded-lg border-2 border-dashed border-border bg-muted/20">
						<p className="text-muted-foreground">ไม่มีข้อมูล</p>
					</div>
				</CardContent>
			</Card>
		)
	}

	return (
		<Card className="group overflow-hidden border-2 transition-all duration-300 hover:shadow-xl">
			<div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
			<CardHeader className="relative border-b bg-gradient-to-r from-muted/30 to-background">
				<CardTitle className="text-lg font-bold">{title}</CardTitle>
				{description && (
					<p className="mt-1 text-sm text-muted-foreground">{description}</p>
				)}
			</CardHeader>
			<CardContent className="relative pt-6">
				<ResponsiveContainer width="100%" height={320}>
					<LineChart
						data={chartData}
						margin={{ top: 10, right: 20, bottom: 10, left: 10 }}
					>
						<defs>
							<linearGradient
								id={`gradient-${color.replace('#', '')}`}
								x1="0"
								y1="0"
								x2="0"
								y2="1"
							>
								<stop offset="5%" stopColor={color} stopOpacity={0.3} />
								<stop offset="95%" stopColor={color} stopOpacity={0} />
							</linearGradient>
						</defs>
						<CartesianGrid
							strokeDasharray="3 3"
							stroke="hsl(var(--border))"
							opacity={0.5}
						/>
						<XAxis
							dataKey="time"
							stroke="hsl(var(--muted-foreground))"
							tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
							axisLine={{ stroke: 'hsl(var(--border))' }}
						/>
						<YAxis
							stroke="hsl(var(--muted-foreground))"
							tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
							axisLine={{ stroke: 'hsl(var(--border))' }}
							domain={yAxisDomain}
							label={{
								value: unit,
								angle: -90,
								position: 'insideLeft',
								style: { textAnchor: 'middle' },
							}}
							tickFormatter={(value) => {
								return `${value.toFixed(0)}${unit}`
							}}
						/>
						<Tooltip
							contentStyle={{
								backgroundColor: 'hsl(var(--card))',
								border: '1px solid hsl(var(--border))',
								borderRadius: '8px',
								boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
								padding: '8px 12px',
							}}
							labelStyle={{
								fontWeight: 600,
								marginBottom: '4px',
							}}
							labelFormatter={(label, payload) => {
								if (payload && payload[0]) {
									return payload[0].payload.fullTime
								}
								return label
							}}
							formatter={(value: number) => {
								return [`${value.toFixed(2)}${unit}`, 'Value']
							}}
						/>
						<Legend wrapperStyle={{ paddingTop: '20px' }} iconType="line" />
						<Line
							type="monotone"
							dataKey="value"
							stroke={color}
							strokeWidth={3}
							dot={false}
							activeDot={{ r: 8, strokeWidth: 2, stroke: color, fill: '#fff' }}
							name={title}
							fill={`url(#gradient-${color.replace('#', '')})`}
						/>
					</LineChart>
				</ResponsiveContainer>
			</CardContent>
		</Card>
	)
}
